/**
 * A live sketch of the brand in its apps, beside the Brand settings: the TV home's top bar with the
 * menu's tabs, a featured item with its Play button, a row with one card in focus, and the phone's
 * bottom tabs. Everything in it comes from the edit in progress (name, colour, tab names in the
 * chosen language), coloured with the apps' own derivation (fills keep the accent; text and focus
 * are shaded until they read on the dark ground). A sketch of the layout, not the native app.
 */

import { useState } from "react";
import Segmented from "@web-ui/components/Segmented";
import { DEFAULT_GROUND, deriveColorTokens } from "@web-ui/brand/color-tokens";

interface PreviewProps {
  name: string;
  accent: string;
  menu: Array<{ pageId: string; label: Record<string, string> }>;
  locales: string[];
  defaultLocale: string;
  languageName: (code: string) => string;
}

export function BrandPreview({ name, accent, menu, locales, defaultLocale, languageName }: PreviewProps) {
  const [device, setDevice] = useState<"tv" | "phone">("tv");
  const [language, setLanguage] = useState(defaultLocale);
  const shown = locales.includes(language) ? language : defaultLocale;
  const valid = /^#[0-9a-fA-F]{6}$/.test(accent);
  const tokens = deriveColorTokens({ accent: valid ? accent : null }, DEFAULT_GROUND);
  // The apps' ink on their dark ground (white), from the same derivation; placeholders are tints of it.
  const ink = deriveColorTokens({ accent: DEFAULT_GROUND }, DEFAULT_GROUND).onAccent;
  const tint = (percent: number) => `color-mix(in srgb, ${ink} ${percent}%, transparent)`;
  const tabs = menu.map((m) => m.label[shown]?.trim() || m.label[defaultLocale]?.trim() || m.pageId);
  const mark = (name.trim()[0] ?? "?").toUpperCase();

  return (
    <section className="card flex flex-col gap-4" aria-label="Preview of your apps">
      <header className="flex flex-col gap-1 ps-card-inset">
        <h3 className="text-sub-title-1">Preview</h3>
        <p className="text-body-2 text-t-secondary">Your name, colour and menu as the apps show them. Changes appear here as you type.</p>
      </header>
      <div className="flex flex-wrap items-center gap-2">
        <Segmented label="Device" size="sm" value={device} onChange={setDevice} options={[{ id: "tv", label: "TV" }, { id: "phone", label: "Phone" }]} />
        {locales.length > 1 ? (
          <Segmented label="Language" size="sm" value={shown} onChange={setLanguage} options={locales.slice(0, 4).map((l) => ({ id: l, label: languageName(l) }))} />
        ) : null}
      </div>

      {device === "tv" ? (
        <div className="aspect-video w-full overflow-hidden rounded-2xl p-[4%]" style={{ background: DEFAULT_GROUND, color: ink }} role="img" aria-label={`TV home of ${name || "your app"}, with the tabs ${tabs.join(", ")}`}>
          <div className="flex h-full flex-col gap-[5%]">
            <div className="flex items-center gap-[3%]">
              <span className="flex aspect-square w-[6%] items-center justify-center rounded-[22%] text-[0.85rem] font-semibold" style={{ background: tokens.accent, color: tokens.onAccent }}>{mark}</span>
              <div className="flex min-w-0 gap-[2%] overflow-hidden">
                {tabs.map((t, i) => (
                  <span key={`${t}-${i}`} className="shrink-0 whitespace-nowrap rounded-full px-[0.5rem] py-[0.15rem] text-[0.62rem]" style={i === 0 ? { background: tokens.accent, color: tokens.onAccent } : { color: tint(70) }}>{t}</span>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-[0.4rem]">
              <span className="h-[0.9rem] w-[38%] rounded" style={{ background: tint(80) }} />
              <span className="h-[0.5rem] w-[52%] rounded" style={{ background: tint(30) }} />
              <span className="mt-[0.4rem] w-fit rounded-full px-[0.8rem] py-[0.25rem] text-[0.7rem]" style={{ background: tokens.accent, color: tokens.onAccent }}>Play</span>
            </div>
            <div className="mt-auto flex flex-col gap-[0.4rem]">
              <span className="text-[0.65rem]" style={{ color: tint(70) }}>Continue watching · <span style={{ color: tokens.accentText }}>See all</span></span>
              <div className="grid grid-cols-5 gap-[2.5%]">
                {Array.from({ length: 5 }, (_, i) => (
                  <span key={i} className="aspect-video rounded-md" style={{ background: tint(15), ...(i === 1 ? { outline: `2px solid ${tokens.accentStroke}`, outlineOffset: 2 } : {}) }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mx-auto aspect-[9/16] w-[60%] max-w-[16rem] overflow-hidden rounded-[1.6rem] border-4 border-s-subtle" style={{ background: DEFAULT_GROUND, color: ink }} role="img" aria-label={`Phone home of ${name || "your app"}, with the tabs ${tabs.join(", ")}`}>
          <div className="flex h-full flex-col">
            <div className="flex items-center gap-2 px-3 pt-4">
              <span className="flex size-6 items-center justify-center rounded-lg text-[0.7rem] font-semibold" style={{ background: tokens.accent, color: tokens.onAccent }}>{mark}</span>
              <span className="truncate text-[0.75rem]">{name || "Your app"}</span>
            </div>
            <div className="mx-3 mt-3 flex aspect-video flex-col justify-end gap-1 rounded-xl p-2" style={{ background: tint(10) }}>
              <span className="h-[0.5rem] w-[60%] rounded" style={{ background: tint(70) }} />
              <span className="w-fit rounded-full px-2 py-[0.1rem] text-[0.6rem]" style={{ background: tokens.accent, color: tokens.onAccent }}>Play</span>
            </div>
            <span className="mx-3 mt-3 text-[0.6rem]" style={{ color: tint(70) }}>Continue watching · <span style={{ color: tokens.accentText }}>See all</span></span>
            <div className="mx-3 mt-1 grid grid-cols-3 gap-1.5">
              {Array.from({ length: 3 }, (_, i) => <span key={i} className="aspect-[2/3] rounded-md" style={{ background: tint(15) }} />)}
            </div>
            <nav className="mt-auto flex justify-around border-t px-1 py-2" style={{ borderColor: tint(10) }} aria-hidden>
              {tabs.slice(0, 5).map((t, i) => (
                <span key={`${t}-${i}`} className="truncate px-1 text-[0.55rem]" style={{ color: i === 0 ? tokens.accentText : tint(60) }}>{t}</span>
              ))}
            </nav>
          </div>
        </div>
      )}
      <p className="text-caption text-t-tertiary">A sketch of the apps' layout. The finished apps also use your typefaces and artwork.</p>
    </section>
  );
}
